'use client';

import Error from '@/components/core/error';
import styles from '@/app/members/[id]/page.module.scss';
import { getMember } from '@/services/members.service';
import IrlMemberContribution from '@/components/page/member-details/member-irl-contributions';
import { UnsavedEditsPageGuard } from '@/components/common/profile/UnsavedEdits';
import {
  ProfileSection,
  SectionEditLockProvider,
  SectionStatusBar,
  useSectionEditLockRegistry,
} from '@/components/common/profile/SectionEditLock';
import { ProfileDetails } from '@/components/page/member-details/ProfileDetails';
import { ContactDetails } from '@/components/page/member-details/ContactDetails';
import { JobSearchStatusDetails } from '@/components/page/member-details/JobSearchStatusDetails';
import { ExperienceDetails } from '@/components/page/member-details/ExperienceDetails';
import { ContributionsDetails } from '@/components/page/member-details/ContributionsDetails';
import { RepositoriesDetails } from '@/components/page/member-details/RepositoriesDetails';
import { OneClickVerification } from '@/components/page/member-details/OneClickVerification';
import { TeamsDetails } from '@/components/page/member-details/TeamsDetails';
import { OfficeHoursDetails } from '@/components/page/member-details/OfficeHoursDetails';
import { InvestorProfileDetails } from '@/components/page/member-details/InvestorProfileDetails';
import { BackButton } from '@/components/ui/BackButton';
import React, { useEffect } from 'react';
import { BookWithOther } from '@/components/page/member-details/BookWithOther';
import { getMemberListForQuery } from '@/app/actions/members.actions';
import qs from 'qs';
import clsx from 'clsx';
import { isDemodaySignUpSource, isMemberAvailableToConnect } from '@/utils/member.utils';
import { useCurrentUserStore } from '@/services/auth/store';
import { getCookiesFromClient } from '@/utils/third-party.helper';
import { useQuery } from '@tanstack/react-query';
import { useSearchParams } from 'next/navigation';
import { AccountCreatedView } from '@/components/page/member-details/AccountCreatedView';

import MemberPageLoader from '@/app/members/[id]/loading';
import Head from 'next/head';
import { MembersQueryKeys } from '@/services/members/constants';
import { MemberCvSection } from '@/components/page/member-details/MemberCvSection/MemberCvSection';
import {
  isJobAspirantMember,
  shouldPlaceCvAfterProfileDetails,
  shouldPlaceCvInDefaultPosition,
  shouldShowInvestorProfile,
} from '@/components/page/member-details/job-aspirant-profile';
import { useGetMemberInvestorSettings } from '@/services/members/hooks/useGetMemberInvestorSettings';
import { ForumActivity } from '@/components/page/member-details/ForumActivity';
import { TeamNewsDetails } from '@/components/page/member-details/TeamNewsDetails';
import { useMemberTeamNewsCard } from '@/components/page/member-details/TeamNewsDetails/hooks/useMemberTeamNewsCard';
import { useIsBelowTabletLandscape } from '@/hooks/useIsBelowTabletLandscape';
import { isAdminUser } from '@/utils/user/isAdminUser';
import { useAffinityAccess } from '@/services/access-control/hooks/useAffinityAccess';
import { useAffinityMember } from '@/services/affinity/hooks/useAffinityMember';
import { useJobEmailProfileLinkEventCapture } from '@/components/page/member-details/hooks';
import { RelationshipDetails } from '@/components/page/member-details/RelationshipDetails';
import { useLoginRedirect } from '@/components/core/login/utils';

/** The member as `getMember` returns it for a signed-out visitor, fetched by the server page (LAB-2922). */
export type ServerMemberResult = Extract<Awaited<ReturnType<typeof getMember>>, { data: unknown }>;

interface MemberDetailsPageProps {
  memberId: string;
  /** The public member from the server, so the profile is in the server HTML. Absent when the server lookup failed. */
  serverMember?: ServerMemberResult;
}

const MemberDetails = ({ memberId, serverMember }: MemberDetailsPageProps) => {
  const searchParams = useSearchParams();
  const goToLogin = useLoginRedirect();

  useJobEmailProfileLinkEventCapture(memberId);

  const { currentUser: viewer } = useCurrentUserStore();
  const isViewerLoggedIn = !!viewer;
  const isViewerAdmin = isAdminUser(viewer);
  const isViewerOwner = !!viewer && viewer.uid === memberId;

  // Check for prefillEmail and returnTo parameters
  const prefillEmail = searchParams.get('prefillEmail');
  const returnTo = searchParams.get('returnTo');
  const shouldShowAccountCreated = !isViewerLoggedIn && prefillEmail && returnTo;
  const {
    data: member,
    isError,
    isLoading,
    isPlaceholderData,
  } = useQuery({
    queryKey: [MembersQueryKeys.GET_MEMBER, memberId, isViewerLoggedIn, viewer?.uid],
    queryFn: () =>
      getMember(
        memberId,
        { with: 'image,skills,location,teamMemberRoles.team' },
        isViewerLoggedIn,
        viewer,
        !isViewerAdmin && !isViewerOwner,
        true,
      ),
    enabled: !!memberId,
    /* The server's public member stands in until this viewer's own fetch lands. Placeholder, never `initialData`: the
       server QueryClient is a module-level singleton, and placeholder data is never written to the cache. It is the raw
       `getMember` result, because `select` runs on placeholder data too. */
    placeholderData: serverMember,
    select: (data) => data?.data?.formattedData,
  });

  /* While the public member stands in, the page renders as a signed-out visitor sees it, whoever is looking. That is
     what the server rendered, so hydration matches; and an owner cannot open an edit form prefilled from the redacted
     public record. Edit controls and private sections appear when the viewer's own fetch lands. */
  const userInfo = isPlaceholderData ? null : viewer;
  const isAdmin = isAdminUser(userInfo);
  const isOwner = !!userInfo && userInfo.uid === memberId;
  const isLoggedIn = !!userInfo;

  const { data: memberInvestorSettings } = useGetMemberInvestorSettings(memberId);
  const { authToken } = getCookiesFromClient();
  const { data: availableToConnectCount } = useQuery({
    queryKey: ['memberList'],
    queryFn: () => getMemberListForQuery(qs.stringify({ hasOfficeHours: true }), 1, 1, authToken),
    enabled: !!authToken,
    select: (data) => data?.total,
  });
  const isAvailableToConnect = isMemberAvailableToConnect(member);
  const { hasAccess: hasAffinityAccess } = useAffinityAccess();
  const { data: affinityData, isLoading: affinityLoading } = useAffinityMember(memberId, hasAffinityAccess);
  const hasAffinityContent =
    hasAffinityAccess && (affinityLoading || (!!affinityData && !affinityData.relationship.empty));
  const showOtherConnectOptions =
    !hasAffinityContent && !isAvailableToConnect && isLoggedIn && userInfo?.rbac?.status === 'APPROVED' && !isOwner;
  // The news card lives in the rail, so its visibility has to open the rail —
  // otherwise a member whose only rail content is news gets no rail at all.
  // Resolved here and in the card itself; both land on the same query entry.
  const isBelowTabletLandscape = useIsBelowTabletLandscape();
  /* One open section at a time, and the status bar for it once it has been
     scrolled away. The sections need no props: the controls row every edit form
     is built from claims the lock itself. See `SectionEditLock`. */
  const sectionEditLock = useSectionEditLockRegistry();
  const { visible: showTeamNews } = useMemberTeamNewsCard({ member, isLoggedIn, userInfo });
  const showSidebar = showOtherConnectOptions || hasAffinityContent || (showTeamNews && !isBelowTabletLandscape);
  const status = member?.rbac?.status;
  const isNewInvestor = status === 'PENDING' && isOwner && isDemodaySignUpSource(member?.signUpSource);

  /* Scroll to top when a member's page finishes loading, or when you navigate
     to a different member.

     **Keyed on the id, not on the `member` object.** The dependency used to be
     `member` itself, which is a new object every time anything patches the
     query cache — and `useUpdateMemberParams` patches it optimistically on
     every inline save. So picking a job search status radio, or saving any
     section that writes through that hook, snapped the page to the top under
     the person's hands mid-interaction. The trigger this effect wants is "a
     different member's page is now on screen", and that is what `loadedMemberId`
     changes on. */
  const loadedMemberId = member && !isLoading ? memberId : null;
  useEffect(() => {
    if (!loadedMemberId) {
      return;
    }
    document.body.scrollTo({
      top: 0,
      left: 0,
      behavior: 'instant',
    });
  }, [loadedMemberId]);

  // Handle login click from AccountCreatedView
  const handleLoginClick = () => {
    // Stay on the same page and add #login hash
    goToLogin();
  };

  // Show AccountCreatedView if user is not logged in and has prefillEmail and returnTo params
  if (shouldShowAccountCreated) {
    return <AccountCreatedView onLoginClick={handleLoginClick} />;
  }

  if (isError) {
    return <Error />;
  }

  if (isLoading) {
    return <MemberPageLoader />;
  }

  if (!member) {
    return <Error title="This member doesn't exist or isn't approved yet" description="Member not found" />;
  }

  function renderPageContent() {
    if (!member) {
      return null;
    }

    const jobAspirant = isJobAspirantMember(member);
    const showInvestorProfile = shouldShowInvestorProfile({
      isOwner,
      isAdmin,
      isInvestor: memberInvestorSettings?.isInvestor,
      isJobAspirant: jobAspirant,
    });
    const isInvestorOnly =
      isNewInvestor || member.rbac.policies?.every((p: { role: string }) => p.role.toLowerCase() === 'investor');
    /* Signed in, and nothing else. Who may actually see a CV is `assertCanView`'s
       answer, which arrives with the query. */
    const showCvSection = isLoggedIn;
    const cvAfterProfile = shouldPlaceCvAfterProfileDetails({
      showCvSection,
      isJobAspirant: jobAspirant,
      isOwner,
    });
    const cvInDefaultPosition = shouldPlaceCvInDefaultPosition({
      showCvSection,
      isJobAspirant: jobAspirant,
      isOwner,
    });
    const cvSection = showCvSection ? (
      <ProfileSection name={isOwner ? 'Your CV' : 'CV'}>
        <MemberCvSection member={member} isOwner={isOwner} />
      </ProfileSection>
    ) : null;

    return (
      <>
        {/* Every card is wrapped, the read-only ones too: while one section is
            being edited the rest of the column is `inert` and faded, and a card
            nobody can open still has to step back with the others. The name is
            what the status bar calls the section it is reporting on, so it is
            the card's own header spelling. See `SectionEditLock`. */}
        <ProfileSection name="Verification">
          <OneClickVerification
            userInfo={userInfo}
            member={member}
            isLoggedIn={isLoggedIn}
            isNewInvestor={isNewInvestor}
          />
        </ProfileSection>
        <ProfileSection name="Profile Details">
          <ProfileDetails userInfo={userInfo} member={member} isLoggedIn={isLoggedIn} />
        </ProfileSection>
        {cvAfterProfile && cvSection}
        {showInvestorProfile && (
          <ProfileSection name="Investor Profile">
            <InvestorProfileDetails
              userInfo={userInfo}
              member={member}
              isLoggedIn={isLoggedIn}
              isInvestor={memberInvestorSettings?.isInvestor}
              useInlineAddTeam
            />
          </ProfileSection>
        )}
        <ProfileSection name="Office Hours">
          <OfficeHoursDetails userInfo={userInfo} member={member} isLoggedIn={isLoggedIn} />
        </ProfileSection>
        <ProfileSection name="Contact Details">
          <ContactDetails userInfo={userInfo} member={member} isLoggedIn={isLoggedIn} />
        </ProfileSection>
        <ProfileSection name="Forum Activity">
          <ForumActivity member={member} userInfo={userInfo} isOwner={isOwner} />
        </ProfileSection>
        <ProfileSection name="Teams">
          <TeamsDetails member={member} isLoggedIn={isLoggedIn} userInfo={userInfo} />
        </ProfileSection>
        {/* Below the two-column breakpoint the rail is hidden, so the card falls
            in here — directly under the teams it describes, as the prototype
            does. Exactly one of the two mounts is ever rendered: two would put
            duplicate data-story-uid nodes on the page and focus restore
            resolves that attribute by querySelector. */}
        {isBelowTabletLandscape && (
          <ProfileSection name="Team News">
            <TeamNewsDetails member={member} isLoggedIn={isLoggedIn} userInfo={userInfo} />
          </ProfileSection>
        )}
        {/* Private to the member, so it is mounted only on their own profile —
            the API omits `jobSearchStatus` for every other viewer anyway, but
            the pill inside promises "only visible to you" and that sentence has
            to be true of the surface, not just of the payload. The card carries
            the same check itself as a backstop.

            Sits directly above Experience, and deliberately OUTSIDE the
            `!isInvestorOnly` block below it: an investor-only member loses the
            Experience and Contributions sections, and their own job search
            status is not one of the things that should go with them. */}
        {isOwner && (
          <ProfileSection name="Job Search Status">
            <JobSearchStatusDetails member={member} />
          </ProfileSection>
        )}
        {/* The document the profile is holding, and the only place it can be
            previewed, replaced or removed.

            Mounted for every signed-in reader rather than gated on `isOwner`,
            because who may see a CV is a question only the API can answer:
            `assertCanView` admits the member, a directory admin, and a lead of a
            team this member applied to — and that last clause is a
            `jobApplication` lookup. So the section asks and draws what comes
            back; everyone else gets `null` and `.section:empty` collapses the
            wrapper, so a reader with no answer sees no gap.

            The owner always gets the section: the resting card with a CV, the
            drop area without one. Job Aspirants, and anyone reading another
            member's CV, pin it under Profile Details. */}
        {cvInDefaultPosition && cvSection}
        {!isInvestorOnly && (
          <>
            {/* The CV importer's second host, and only half of it: the header's
                "Update from CV", never a drop area. The section above holds one
                permanently, and one page carrying two boxes to drop a file into
                is the choice nobody can get right or wrong.

                The section still refuses even that to anyone who cannot edit
                this profile — `canEditMemberProfile`, the same gate its Add and
                Edit controls use — so this prop only has to say what the host
                offers. */}
            <ProfileSection name="Experience">
              <ExperienceDetails
                userInfo={userInfo}
                member={member}
                isLoggedIn={isLoggedIn}
                cvImportSurface="header-only"
              />
            </ProfileSection>
            <ProfileSection name="Project Contributions">
              <ContributionsDetails userInfo={userInfo} member={member} isLoggedIn={isLoggedIn} />
            </ProfileSection>
          </>
        )}

        {member.eventGuests.length > 0 && (
          <ProfileSection name="IRL Contributions" className={styles?.memberDetail__irlContribution}>
            <IrlMemberContribution member={member} userInfo={userInfo} />
          </ProfileSection>
        )}
        {!isInvestorOnly && (
          <ProfileSection name="Repositories">
            <RepositoriesDetails userInfo={userInfo} member={member} isLoggedIn={isLoggedIn} />
          </ProfileSection>
        )}
      </>
    );
  }

  return (
    /* Leaving with a section still open and edited asks first — see
       `UnsavedEditsPageGuard`. Wraps the whole page rather than the section
       column, because the links that leave (the navbar) are outside it, and
       because the guard's own prompt has to render somewhere. The sections need
       no changes: the controls component each edit form is built from registers
       itself. */
    <UnsavedEditsPageGuard>
      <Head>
        <title>{`${member?.name} | Protocol Labs Directory`}</title>
      </Head>
      <SectionEditLockProvider value={sectionEditLock}>
        <div className={styles?.memberDetail}>
          <div
            className={clsx(styles.container, {
              [styles.singleColumn]: !showSidebar,
            })}
          >
            <div className={styles.content}>
              <BackButton to={`/members`} />
              <div
                className={clsx(styles?.memberDetail__container, {
                  [styles.centered]: isAvailableToConnect || isOwner,
                })}
              >
                {renderPageContent()}
              </div>
            </div>
            {showSidebar && (
              <div className={styles.desktopOnly}>
                <div style={{ visibility: 'hidden' }}>
                  <BackButton to={`/members`} />
                </div>
                {hasAffinityAccess && <RelationshipDetails memberUid={memberId} />}
                {!isBelowTabletLandscape && (
                  <TeamNewsDetails member={member} isLoggedIn={isLoggedIn} userInfo={userInfo} />
                )}
                {showOtherConnectOptions && <BookWithOther count={availableToConnectCount} member={member} />}
              </div>
            )}
          </div>

          {/* {userInfo.uid === member.id && (
          <>
            <SubscribeToRecommendationsWidget userInfo={userInfo} />
            <UpcomingEventsWidget userInfo={userInfo} />
          </>
        )} */}
        </div>
        <SectionStatusBar />
      </SectionEditLockProvider>
    </UnsavedEditsPageGuard>
  );
};

export default MemberDetails;
